import React, { useState, useEffect } from 'react';
import {
  Container,
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Button,
  LinearProgress,
  IconButton,
  Tooltip,
  Divider
} from '@mui/material';
import {
  Refresh,
  CheckCircle,
  Warning,
  Error as ErrorIcon,
  Security,
  Speed,
  Storage,
  Memory,
  NetworkCheck,
  Timeline,
  Visibility,
  VisibilityOff
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { getApiUrl } from '../utils/api';

/** Explains non-healthy monitoring chips (backend sends status_detail / status_summary). */
function MonitoringStatusExplanation({
  severity,
  text,
}: {
  severity: 'error' | 'warning' | 'info';
  text: string | null | undefined;
}) {
  const t = text?.trim();
  if (!t) return null;
  return (
    <Alert severity={severity} sx={{ mt: 1.5 }} variant="outlined">
      <Typography variant="body2" component="div" sx={{ whiteSpace: 'pre-wrap' }}>
        {t}
      </Typography>
    </Alert>
  );
}

function alertSeverityForStatus(status: string | undefined): 'error' | 'warning' | 'info' {
  const s = (status || '').toLowerCase();
  if (s === 'critical') return 'error';
  if (s === 'degraded' || s === 'unhealthy') return 'warning';
  return 'info';
}

/** When backend omits status_summary (older API), derive text from nested status_detail fields. */
function explainOverallStatus(health: SystemHealth | undefined): string | undefined {
  if (!health) return undefined;
  const direct = health.status_summary?.trim();
  if (direct) return direct;
  const st = (health.status || '').toLowerCase();
  if (st !== 'critical' && st !== 'degraded' && st !== 'unhealthy') return undefined;
  const bits: string[] = [];
  const push = (label: string, detail?: string | null) => {
    const d = detail?.trim();
    if (d) bits.push(`${label}: ${d}`);
  };
  push('Server resources', health.system_resources?.status_detail);
  push('Database', health.database_health?.status_detail || health.database_health?.error || null);
  push('Alerts', health.alert_system_health?.status_detail);
  if (bits.length) return bits.join('\n\n');
  return 'Overall status is not healthy, but the server did not include an explanation. Check Network → monitoring/dashboard (system_health) or deploy the latest API.';
}

interface SystemHealth {
  status: string;
  timestamp: string;
  /** Rollup of why overall status is degraded/critical */
  status_summary?: string | null;
  uptime: {
    uptime_formatted: string;
    uptime_seconds: number;
  };
  system_resources: {
    status: string;
    status_detail?: string | null;
    cpu: {
      usage_percent: number;
      count: number;
    };
    memory: {
      total_gb: number;
      available_gb: number;
      usage_percent: number;
    };
    disk: {
      total_gb: number;
      free_gb: number;
      usage_percent: number;
    };
  };
  database_health: {
    status: string;
    status_detail?: string | null;
    error?: string | null;
    connection_time_ms?: number | null;
    query_time_ms?: number | null;
  };
  alert_system_health: {
    status: string;
    status_detail?: string | null;
    total_alerts: number;
    active_alerts: number;
  };
}

interface SecurityMetrics {
  user_activity: {
    new_users_24h: number;
    active_sessions: number;
  };
  security_events: {
    failed_logins_24h: number;
    suspicious_activity: number;
  };
  system_security: {
    ssl_enabled: boolean;
    rate_limiting_active: boolean;
    input_validation_active: boolean;
    sql_injection_protection: boolean;
  };
}

interface OperationalMetrics {
  customers: {
    total: number;
    active: number;
    inactive: number;
  };
  checkins: {
    total: number;
    successful: number;
    success_rate_percent: number;
  };
  sms: {
    sent_24h: number;
    delivery_rate: number;
  };
  services: {
    [key: string]: string;
  };
}

interface AlertStatistics {
  by_status: {
    [key: string]: number;
  };
  by_severity: {
    [key: string]: number;
  };
}

interface DashboardData {
  system_health: SystemHealth;
  security_metrics: SecurityMetrics;
  operational_metrics: OperationalMetrics;
  alert_statistics: AlertStatistics;
  response_time_ms: number;
  timestamp: string;
}

const MonitoringDashboard: React.FC = () => {
  const { token } = useAuth();
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(true);

  const fetchDashboardData = async () => {
    if (!token) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };
      
      const response = await fetch(`${getApiUrl()}/monitoring/dashboard`, { headers });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const dashboardData = await response.json();
      setData(dashboardData);
      setLastRefresh(new Date());
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError('Failed to load monitoring data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
    
    if (autoRefresh) {
      const interval = setInterval(fetchDashboardData, 30000); // Refresh every 30 seconds
      return () => clearInterval(interval);
    }
  }, [token, autoRefresh]);

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'healthy':
        return 'success';
      case 'degraded':
        return 'warning';
      case 'critical':
      case 'unhealthy':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status.toLowerCase()) {
      case 'healthy':
        return <CheckCircle color="success" />;
      case 'degraded':
        return <Warning color="warning" />;
      case 'critical':
      case 'unhealthy':
        return <ErrorIcon color="error" />;
      default:
        return <CheckCircle />;
    }
  };

  if (loading && !data) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          System Monitoring Dashboard
        </Typography>
        <Box>
          <Tooltip title={autoRefresh ? "Disable auto-refresh" : "Enable auto-refresh"}>
            <IconButton onClick={() => setAutoRefresh(!autoRefresh)}>
              {autoRefresh ? <Visibility /> : <VisibilityOff />}
            </IconButton>
          </Tooltip>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={fetchDashboardData}
            disabled={loading}
            sx={{ ml: 1 }}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {lastRefresh && (
        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
          Last updated: {lastRefresh.toLocaleTimeString()}
        </Typography>
      )}

      {data && (
        <>
          {/* System Health Overview */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
            <Card sx={{ flex: '1 1 300px', minWidth: 0 }}>
              <CardContent>
                <Box display="flex" alignItems="center" mb={1}>
                  <Box sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
                    {getStatusIcon(data.system_health?.status || 'unknown')}
                  </Box>
                  <Typography variant="h6">System Status</Typography>
                </Box>
                <Chip
                  label={data.system_health?.status?.toUpperCase() || 'UNKNOWN'}
                  color={getStatusColor(data.system_health?.status || 'unknown') as any}
                  icon={getStatusIcon(data.system_health?.status || 'unknown')}
                  size="small"
                />
                <MonitoringStatusExplanation
                  severity={alertSeverityForStatus(data.system_health?.status)}
                  text={explainOverallStatus(data.system_health)}
                />
                <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                  Uptime: {data.system_health?.uptime?.uptime_formatted || 'N/A'}
                </Typography>
              </CardContent>
            </Card>

            <Card sx={{ flex: '1 1 300px', minWidth: 0 }}>
              <CardContent>
                <Box display="flex" alignItems="center" mb={1}>
                  <Memory color="primary" sx={{ mr: 1 }} />
                  <Typography variant="h6">CPU Usage</Typography>
                </Box>
                <Typography variant="h4" color="primary">
                  {data.system_health?.system_resources?.cpu?.usage_percent || 0}%
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={data.system_health?.system_resources?.cpu?.usage_percent || 0}
                  sx={{ mt: 1 }}
                />
                <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                  {data.system_health?.system_resources?.cpu?.count || 0} cores
                </Typography>
              </CardContent>
            </Card>

            <Card sx={{ flex: '1 1 300px', minWidth: 0 }}>
              <CardContent>
                <Box display="flex" alignItems="center" mb={1}>
                  <Storage color="primary" sx={{ mr: 1 }} />
                  <Typography variant="h6">Memory Usage</Typography>
                </Box>
                <Typography variant="h4" color="primary">
                  {data.system_health?.system_resources?.memory?.usage_percent || 0}%
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={data.system_health?.system_resources?.memory?.usage_percent || 0}
                  sx={{ mt: 1 }}
                />
                <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                  {data.system_health?.system_resources?.memory?.available_gb || 0}GB available
                </Typography>
              </CardContent>
            </Card>

            <Card sx={{ flex: '1 1 300px', minWidth: 0 }}>
              <CardContent>
                <Box display="flex" alignItems="center" mb={1}>
                  <NetworkCheck color="primary" sx={{ mr: 1 }} />
                  <Typography variant="h6">Database</Typography>
                </Box>
                <Chip
                  label={data.system_health?.database_health?.status?.toUpperCase() || 'UNKNOWN'}
                  color={getStatusColor(data.system_health?.database_health?.status || 'unknown') as any}
                  icon={getStatusIcon(data.system_health?.database_health?.status || 'unknown')}
                  size="small"
                />
                <MonitoringStatusExplanation
                  severity={alertSeverityForStatus(data.system_health?.database_health?.status)}
                  text={
                    data.system_health?.database_health?.status_detail ||
                    data.system_health?.database_health?.error
                  }
                />
                <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                  Response:{' '}
                  {(() => {
                    const db = data.system_health?.database_health;
                    const ms =
                      db?.connection_time_ms ?? db?.query_time_ms ?? null;
                    return ms != null && ms !== undefined ? `${ms} ms` : 'N/A';
                  })()}
                </Typography>
              </CardContent>
            </Card>
          </Box>

          {/* Detailed Metrics */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
            {/* Security Metrics */}
            <Card sx={{ flex: '1 1 500px', minWidth: 0 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  <Security sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Security Overview
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableBody>
                      <TableRow>
                        <TableCell>New Users (24h)</TableCell>
                        <TableCell>{data.security_metrics?.user_activity?.new_users_24h || 'N/A'}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Active Sessions</TableCell>
                        <TableCell>{data.security_metrics?.user_activity?.active_sessions || 'N/A'}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Failed Logins (24h)</TableCell>
                        <TableCell>{data.security_metrics?.security_events?.failed_logins_24h || 'N/A'}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Suspicious Activity</TableCell>
                        <TableCell>{data.security_metrics?.security_events?.suspicious_activity || 'N/A'}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
                
                <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                  Security Features
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {data.security_metrics?.system_security ? 
                    Object.entries(data.security_metrics.system_security).map(([key, value]) => (
                      <Chip
                        key={key}
                        label={key.replace(/_/g, ' ')}
                        color={value ? 'success' : 'error'}
                        size="small"
                        icon={value ? <CheckCircle /> : <ErrorIcon />}
                      />
                    ))
                    : <Typography variant="body2" color="textSecondary">No security data available</Typography>
                  }
                </Box>
              </CardContent>
            </Card>

            {/* Operational Metrics */}
            <Card sx={{ flex: '1 1 500px', minWidth: 0 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  <Timeline sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Operational Metrics
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableBody>
                      <TableRow>
                        <TableCell>Total Customers</TableCell>
                        <TableCell>{data.operational_metrics?.customers?.total || 'N/A'}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Active Customers</TableCell>
                        <TableCell>{data.operational_metrics?.customers?.active || 'N/A'}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Check-in Success Rate</TableCell>
                        <TableCell>{data.operational_metrics?.checkins?.success_rate_percent ? `${data.operational_metrics.checkins.success_rate_percent}%` : 'N/A'}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>SMS Sent (24h)</TableCell>
                        <TableCell>{data.operational_metrics?.sms?.sent_24h || 'N/A'}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>SMS Delivery Rate</TableCell>
                        <TableCell>{data.operational_metrics?.sms?.delivery_rate ? `${data.operational_metrics.sms.delivery_rate}%` : 'N/A'}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
                
                <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                  Service Status
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {data.operational_metrics?.services ? 
                    Object.entries(data.operational_metrics.services).map(([service, status]) => (
                      <Chip
                        key={service}
                        label={service.replace(/_/g, ' ')}
                        color={getStatusColor(status) as any}
                        size="small"
                        icon={getStatusIcon(status)}
                      />
                    ))
                    : <Typography variant="body2" color="textSecondary">No service data available</Typography>
                  }
                </Box>
              </CardContent>
            </Card>

            {/* Alert Statistics */}
            <Card sx={{ flex: '1 1 100%', minWidth: 0 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Alert Statistics (24h)
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                  <Box sx={{ flex: '1 1 400px', minWidth: 0 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      By Status
                    </Typography>
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Status</TableCell>
                            <TableCell>Count</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {data.alert_statistics?.by_status ? 
                            Object.entries(data.alert_statistics.by_status).map(([status, count]) => (
                              <TableRow key={status}>
                                <TableCell>
                                  <Chip
                                    label={status}
                                    size="small"
                                    color={getStatusColor(status) as any}
                                  />
                                </TableCell>
                                <TableCell>{count}</TableCell>
                              </TableRow>
                            ))
                            : <TableRow><TableCell colSpan={2}>No alert data available</TableCell></TableRow>
                          }
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                  
                  <Box sx={{ flex: '1 1 400px', minWidth: 0 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      By Severity
                    </Typography>
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Severity</TableCell>
                            <TableCell>Count</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {data.alert_statistics?.by_severity ? 
                            Object.entries(data.alert_statistics.by_severity).map(([severity, count]) => (
                              <TableRow key={severity}>
                                <TableCell>
                                  <Chip
                                    label={severity}
                                    size="small"
                                    color={
                                      severity === 'high' ? 'error' :
                                      severity === 'medium' ? 'warning' : 'success'
                                    }
                                  />
                                </TableCell>
                                <TableCell>{count}</TableCell>
                              </TableRow>
                            ))
                            : <TableRow><TableCell colSpan={2}>No alert data available</TableCell></TableRow>
                          }
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Box>

          {/* Performance Metrics */}
          <Card sx={{ mt: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <Speed sx={{ mr: 1, verticalAlign: 'middle' }} />
                Performance Metrics
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Dashboard response time: {data.response_time_ms || 0}ms
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Data timestamp: {data.timestamp ? new Date(data.timestamp).toLocaleString() : 'N/A'}
              </Typography>
            </CardContent>
          </Card>
        </>
      )}
    </Container>
  );
};

export default MonitoringDashboard;
